import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Avatar,
  Grid,
  Card,
  CardContent,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MenuBook as MenuBookIcon,
  School as SchoolIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function AdminSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    class_id: '',
    teacher_id: '',
    description: '',
  });

  const BRAND = {
    a: '#667eea',
    b: '#764ba2',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    surface: 'rgba(255, 255, 255, 0.95)',
    stroke: 'rgba(255,255,255,0.18)',
    ink: '#0f172a',
    muted: '#64748b',
  };

  useEffect(() => {
    fetchSubjects();
    fetchClasses();
    fetchTeachers();
  }, []);

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (err) {
      setError('Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (err) {
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teachers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeachers(response.data);
    } catch (err) {
    }
  };

  const handleOpenDialog = (subject = null) => {
    if (subject) {
      setEditingSubject(subject);
      setFormData({
        name: subject.name,
        code: subject.code,
        class_id: subject.class_id || '',
        teacher_id: subject.teacher_id || '',
        description: subject.description || '',
      });
    } else {
      setEditingSubject(null);
      setFormData({
        name: '',
        code: '',
        class_id: '',
        teacher_id: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubject(null);
    setFormData({ name: '', code: '', class_id: '', teacher_id: '', description: '' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (editingSubject) {
        await axios.put(`${API_URL}/subjects/${editingSubject.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Subject updated successfully');
      } else {
        await axios.post(`${API_URL}/subjects`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Subject created successfully');
      }
      handleCloseDialog();
      fetchSubjects();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save subject');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subject? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/subjects/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Subject deleted successfully');
        fetchSubjects();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete subject');
      }
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.user?.name : 'Not Assigned';
  };

  const getClassName = (classId) => {
    const classItem = classes.find(c => c.id === classId);
    return classItem ? `${classItem.name} - ${classItem.section}` : 'Not Assigned';
  };

  const stats = [
    { 
      title: 'Total Subjects', 
      count: subjects.length, 
      icon: <MenuBookIcon />, 
      color: BRAND.a 
    },
    { 
      title: 'Assigned Teachers', 
      count: subjects.filter(s => s.teacher_id).length, 
      icon: <PersonIcon />, 
      color: BRAND.b 
    },
    { 
      title: 'Assigned to Classes', 
      count: subjects.filter(s => s.class_id).length, 
      icon: <SchoolIcon />, 
      color: BRAND.a 
    },
    { 
      title: 'Unassigned', 
      count: subjects.filter(s => !s.teacher_id || !s.class_id).length, 
      icon: <MenuBookIcon />, 
      color: '#f59e0b' 
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: BRAND.gradient,
      py: { xs: 3, md: 4 },
      px: { xs: 2, md: 4 },
    }}>
      <Container maxWidth="xl">
        {/* Header Card */}
        <Box sx={{ 
          mb: 4, 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 3, md: 4 }, 
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${BRAND.stroke}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                width: 64, 
                height: 64, 
                background: BRAND.gradient,
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
              }}>
                <MenuBookIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h3" fontWeight="800" sx={{ 
                  color: BRAND.ink,
                  mb: 0.5,
                  fontSize: { xs: '1.75rem', md: '2.5rem' }
                }}>
                  Manage Subjects
                </Typography>
                <Typography variant="body1" sx={{ color: BRAND.muted }}>
                  Create and manage subjects, assign teachers and classes
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: BRAND.gradient,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                }
              }}
            >
              Add Subject
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                transition: 'all 0.3s ease',
                border: `1px solid ${BRAND.stroke}`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(stat.color, 0.1),
                      display: 'flex'
                    }}>
                      <Box sx={{ color: stat.color, fontSize: 28 }}>
                        {stat.icon}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="h3" fontWeight="800" sx={{ color: stat.color, mb: 1 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Subjects Table */}
        <Paper sx={{ 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: `1px solid ${BRAND.stroke}`,
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(BRAND.a, 0.08) }}>
                  <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Subject Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Subject Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Class</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Teacher</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: BRAND.ink }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <MenuBookIcon sx={{ fontSize: 64, color: BRAND.muted, mb: 2 }} />
                      <Typography variant="h6" sx={{ color: BRAND.muted }}>
                        No subjects found. Create your first subject!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  subjects.map((subject) => (
                    <TableRow 
                      key={subject.id}
                      sx={{ 
                        '&:hover': { bgcolor: alpha(BRAND.a, 0.04) },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Chip 
                          label={subject.code} 
                          size="small" 
                          sx={{ 
                            fontWeight: 700,
                            bgcolor: alpha(BRAND.a, 0.1),
                            color: BRAND.a
                          }} 
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{subject.name}</TableCell>
                      <TableCell>
                        {subject.class_id ? (
                          <Chip 
                            icon={<SchoolIcon />}
                            label={getClassName(subject.class_id)} 
                            size="small" 
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Not Assigned" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {subject.teacher_id ? (
                          <Chip 
                            icon={<PersonIcon />}
                            label={getTeacherName(subject.teacher_id)} 
                            size="small" 
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Not Assigned" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={subject.status ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={subject.status ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog(subject)}
                          sx={{ 
                            color: BRAND.a,
                            '&:hover': { bgcolor: alpha(BRAND.a, 0.1) }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(subject.id)}
                          sx={{ 
                            color: '#ef4444',
                            '&:hover': { bgcolor: alpha('#ef4444', 0.1) }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add/Edit Subject Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: BRAND.gradient,
            color: 'white',
            fontWeight: 700,
            fontSize: '1.5rem'
          }}>
            {editingSubject ? 'Edit Subject' : 'Add New Subject'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    helperText="e.g., MATH101, ENG201"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Class"
                    value={formData.class_id}
                    onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                  >
                    <MenuItem value="">Not Assigned</MenuItem>
                    {classes.map((classItem) => (
                      <MenuItem key={classItem.id} value={classItem.id}>
                        {classItem.name} - {classItem.section}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Teacher"
                    value={formData.teacher_id}
                    onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                  >
                    <MenuItem value="">Not Assigned</MenuItem>
                    {teachers.map((teacher) => (
                      <MenuItem key={teacher.id} value={teacher.id}>
                        {teacher.user?.name} ({teacher.employee_id})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={loading}
                sx={{
                  background: BRAND.gradient,
                  fontWeight: 600,
                  px: 3
                }}
              >
                {editingSubject ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Box>
  );
}

export default AdminSubjects;
